import { toast } from 'sonner'
import { useForm } from 'react-hook-form'

import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'
import { TIME_IN_MS } from 'src/constants'

import { useManageFuelHistory } from 'src/hooks/useManageFuelHistory'
import { getFormattedTitle } from 'src/helpers/getFromatedTittle'

import {
  getFuelTypeOptions,
  getSimpleFields,
} from 'src/pages/FuelTracking/helpers'
import { FuelType } from 'src/pages/FuelTracking/enums'
import { queryClient } from 'src/features/AppProviders/constants'
import { AlertUpdateWindowProps } from 'src/features/AlertFuelUpdateWindow/types'
import { FuelTrackProps } from 'src/api/FuelTracking/types'

export const useAlertFuelUpdateWindow = ({
  itemId,
  toggle,
  totalCost,
  pricePerLiter,
  address,
  fuelQuantity,
  fuelType,
  odometer,
  dateOfRefuel,
  distanceUnit,
  currency,
}: Omit<AlertUpdateWindowProps, 'isOpen'>) => {
  const { translations } = useLocales()

  const { updateFuelHistory, isLoadingGeneral } = useManageFuelHistory()

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      date: new Date(dateOfRefuel.seconds * TIME_IN_MS.SECOND),
      address,
      odometer: odometer,
      fuelQuantity,
      pricePerLiter: pricePerLiter,
      fuelType,
      totalCost: totalCost,
    },
  })

  const onUpdate = (): void => {
    const {
      fuelQuantity,
      totalCost,
      pricePerLiter,
      date,
      odometer,
      address,
      fuelType,
    } = getValues()

    const updatedItem: FuelTrackProps = {
      fuelQuantity: Number(fuelQuantity) || 0,
      totalCost: Number(totalCost) || 0,
      pricePerLiter: Number(pricePerLiter) || 0,
      fuelType: fuelType || FuelType.PETROL95,
      address: address.trim() || '',
      odometer: Number(odometer) || 0,
      dateOfRefuel: date || new Date(),
      dateOFLastEdit: new Date(),
      distanceUnit,
      currency,
    }

    updateFuelHistory(
      { payload: updatedItem, id: itemId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.FUEL_HISTORY],
          })
          toggle()
          toast(translations.trackFuel.historyFuel.toast.success.title, {
            description:
              translations.trackFuel.historyFuel.toast.success.description,
            action: {
              label:
                translations.trackFuel.historyFuel.toast.success.action.label,
              onClick: () => toast.dismiss(),
            },
          })
        },
        onError: err => {
          toast(translations.trackFuel.historyFuel.toast.error.title, {
            description:
              translations.trackFuel.historyFuel.toast.error.description + err,
            action: {
              label:
                translations.trackFuel.historyFuel.toast.error.action.label,
              onClick: () => toast.dismiss(),
            },
          })
        },
      },
    )
  }

  const formattedTitle = (name: string, title: string): string => {
    return getFormattedTitle(name, title, currency.toUpperCase(), distanceUnit)
  }

  return {
    title: translations.trackFuel.historyFuel.alert.update.title,
    cancel: translations.trackFuel.historyFuel.alert.cancel,
    updateBtn: translations.trackFuel.historyFuel.alert.update.updateBtn,
    isLoadingGeneral,
    control,
    handleSubmit,
    onUpdate,
    fieldsLocale: translations.trackFuel.addInfoFuel,
    errors,
    setValue,
    fuelTypeOptions: getFuelTypeOptions(translations.trackFuel),
    fields: getSimpleFields(translations.trackFuel),
    formattedTitle,
  }
}
